/**
 * Fontes exportáveis da Gestão Rastreador: Rastreadores, Chips, Kits e Movimentações
 * -----------------------------------------------------------------------------
 * Sem RPC dedicada: reaproveitam `get_stock_items` e `get_stock_movements` com
 * `p_export_mode = true`, que valida permissão (assert_export_allowed com a
 * mesma chave da fonte), limite de uso e libera blocos de até 500 linhas.
 *
 * RELEASE: 2026-10-10 v1.1.0 - Atualiza os nomes visíveis das fontes de exportação.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (Fase 5 do Estoque).
 */
import { createLogger } from '@/lib/logger';
import { stockStatusLabel, type StockItemKind, type StockItemRow, type StockItemsFilters } from '@/hooks/useStockItems';
import { movementsRpcArgs, stockRpc, type StockMovementRow, type StockMovementsFilters, type StockPlace } from '@/hooks/useStockMovements';
import { EXPORT_ROW_LIMIT, type ExportColumn, type ExportSource } from '../types';

const logger = createLogger('ExportStock');
const LOTE = 500;
const PLACE_TYPE = { location: 'Estoque', provider: 'Prestador', customer: 'Cliente', carrier: 'Transportadora' } as const;
const placeText = (p: StockPlace | null | undefined) => (p ? [p.label, p.customer_name].filter(Boolean).join(' · ') : null);

/** Lê em blocos até o limite padrão de exportação. */
async function lerTudo<T>(fn: string, args: Record<string, unknown>, tenantId: string): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; out.length < EXPORT_ROW_LIMIT; page++) {
    const { data, error } = await stockRpc(fn, { ...args, p_page: page, p_page_size: LOTE, p_export_mode: true, p_tenant_hint: tenantId });
    if (error) {
      logger.error('Falha na exportação do estoque', { fn, error: error.message });
      throw new Error(error.message);
    }
    const items = ((data as { items?: T[] })?.items ?? []) as T[];
    out.push(...items);
    if (items.length < LOTE) break;
  }
  return out.slice(0, EXPORT_ROW_LIMIT);
}

/** Colunas comuns de local atual. */
const COL_LOCAL: ExportColumn[] = [
  { key: 'local_tipo', label: 'Tipo de local', group: 'Local', defaultSelected: true },
  { key: 'local', label: 'Local atual', group: 'Local', defaultSelected: true },
  { key: 'local_desde', label: 'No local desde', group: 'Local', kind: 'datetime', defaultSelected: true },
];

type ItemsParams = Omit<StockItemsFilters, 'page' | 'pageSize'>;

/** Fábrica das fontes de itens. */
function itemsSource(kind: StockItemKind, id: string, label: string, columns: ExportColumn[], map: (r: StockItemRow) => Record<string, unknown>): ExportSource<ItemsParams> {
  return {
    id,
    label,
    moduleKey: 'estoque',
    fileBaseName: id.replace('stock_', 'estoque_'),
    columns: [...columns, { key: 'situacao', label: 'Situação', group: 'Item', defaultSelected: true }, ...COL_LOCAL],
    fetchRows: async (tenantId, p) => {
      const rows = await lerTudo<StockItemRow & { place?: StockPlace | null }>('get_stock_items', {
        p_kind: kind, p_search: p.search || null, p_status: p.status === 'all' ? null : p.status,
        p_kit_filter: kind === 'kit' ? 'all' : p.kit, p_active: p.active,
      }, tenantId);
      return rows.map((r) => ({
        ...map(r),
        situacao: stockStatusLabel(kind, r.status),
        local_tipo: r.place ? PLACE_TYPE[r.place.type] : null,
        local: placeText(r.place),
        local_desde: r.place?.since ?? null,
      }));
    },
  };
}

export const stockTrackersExportSource = itemsSource('tracker', 'stock_trackers', 'Gestão Rastreador — Rastreadores', [
  { key: 'imei', label: 'IMEI', group: 'Item', defaultSelected: true },
  { key: 'modelo', label: 'Modelo', group: 'Item', defaultSelected: true },
  { key: 'fabricante', label: 'Fabricante', group: 'Item', defaultSelected: true },
  { key: 'esn', label: 'ESN', group: 'Item', defaultSelected: true },
  { key: 'fornecedor', label: 'Fornecedor', group: 'Item', defaultSelected: true },
  { key: 'kit', label: 'Kit', group: 'Item', defaultSelected: true },
], (r) => ({ imei: r.imei, modelo: r.model_name, fabricante: r.manufacturer_name, esn: r.esn, fornecedor: r.supplier_name, kit: r.kit_code }));

export const stockChipsExportSource = itemsSource('chip', 'stock_chips', 'Gestão Rastreador — Chips', [
  { key: 'iccid', label: 'ICCID', group: 'Item', defaultSelected: true },
  { key: 'linha', label: 'Linha', group: 'Item', defaultSelected: true },
  { key: 'imsi', label: 'IMSI', group: 'Item', defaultSelected: false },
  { key: 'plano', label: 'Plano de dados', group: 'Item', defaultSelected: true },
  { key: 'pacote_mb', label: 'Pacote (MB)', group: 'Item', defaultSelected: true },
  { key: 'fornecedor', label: 'Fornecedor', group: 'Item', defaultSelected: true },
  { key: 'operadora', label: 'Operadora', group: 'Item', defaultSelected: true },
  { key: 'kit', label: 'Kit', group: 'Item', defaultSelected: true },
], (r) => ({ iccid: r.iccid, linha: r.line_number, imsi: r.imsi, plano: r.data_plan_name ?? null, pacote_mb: r.data_plan_package_mb ?? null, fornecedor: r.data_plan_supplier_name ?? null, operadora: (r.data_plan_carriers as string[] | null)?.join(', ') || r.carrier_name, kit: r.kit_code }));

export const stockKitsExportSource = itemsSource('kit', 'stock_kits', 'Gestão Rastreador — Kits', [
  { key: 'codigo', label: 'Código', group: 'Item', defaultSelected: true },
  { key: 'imei', label: 'IMEI', group: 'Item', defaultSelected: true },
  { key: 'iccid', label: 'ICCID', group: 'Item', defaultSelected: true },
  { key: 'linha', label: 'Linha', group: 'Item', defaultSelected: true },
  { key: 'montado', label: 'Montado em', group: 'Item', kind: 'datetime', defaultSelected: true },
  { key: 'desmontado', label: 'Desmontado em', group: 'Item', kind: 'datetime', defaultSelected: false },
], (r) => ({ codigo: r.code, imei: r.imei, iccid: r.iccid, linha: r.line_number, montado: r.formed_at, desmontado: r.dissolved_at }));

const ITEM = { tracker: 'Rastreador', chip: 'Chip', kit: 'Kit' } as const;

export const stockMovementsExportSource: ExportSource<Omit<StockMovementsFilters, 'page' | 'pageSize'>> = {
  id: 'stock_movements',
  label: 'Gestão Rastreador — Movimentações',
  moduleKey: 'estoque',
  fileBaseName: 'estoque_movimentacoes',
  columns: [
    { key: 'data', label: 'Data', group: 'Movimentação', kind: 'datetime', defaultSelected: true },
    { key: 'tipo_item', label: 'Tipo de item', group: 'Movimentação', defaultSelected: true },
    { key: 'item', label: 'Item', group: 'Movimentação', defaultSelected: true },
    { key: 'imei', label: 'IMEI', group: 'Item', defaultSelected: true },
    { key: 'iccid', label: 'ICCID', group: 'Item', defaultSelected: true },
    { key: 'evento', label: 'Evento', group: 'Movimentação', defaultSelected: true },
    { key: 'de', label: 'De', group: 'Movimentação', defaultSelected: true },
    { key: 'para_tipo', label: 'Tipo de destino', group: 'Movimentação', defaultSelected: true },
    { key: 'para', label: 'Para', group: 'Movimentação', defaultSelected: true },
    { key: 'obs', label: 'Observações', group: 'Movimentação', defaultSelected: false },
    { key: 'por', label: 'Por', group: 'Movimentação', defaultSelected: true },
  ],
  fetchRows: async (tenantId, p) => {
    const rows = await lerTudo<StockMovementRow>('get_stock_movements', movementsRpcArgs(p), tenantId);
    return rows.map((m) => ({
      data: m.created_at, tipo_item: ITEM[m.item_type], item: m.kit_code ?? m.item_label,
      imei: m.imei, iccid: m.iccid,
      evento: m.event_type === 'kit_dissolved' ? 'Desmontagem de kit' : 'Movimentação',
      de: placeText(m.from_place), para_tipo: m.to_place ? PLACE_TYPE[m.to_place.type] : null, para: placeText(m.to_place),
      obs: m.notes, por: m.created_by_name,
    }));
  },
};
